import React, { useState, useEffect } from 'react';
import api from '../../../components/api';
import Popup from '../../UI/Popup';
import '../../../styles/form.css';
import './OutlineGeneratorPopup.css';

const OutlineGeneratorPopup = ({ project, onClose, onOutlineGenerated }) => {
    const [description, setDescription] = useState(project.description || '');
    const [length, setLength] = useState(2); // Default medium length
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);

    useEffect(() => {
        // Initialize with project description if available
        if (project.description) {
            setDescription(project.description);
        }
    }, [project]);

    const handleConfirmGenerate = async () => {
        setShowConfirmation(false);
        
        if (!description.trim()) {
            setError('Please provide a description for your project.');
            return;
        }

        setIsGenerating(true);
        setError('');

        try {
            // First, update the project description if it changed
            if (description !== project.description) {
                await api.post('/update_write_settings', {
                    project_id: project.id,
                    description
                });
            }

            // Generate the outline
            const response = await api.post('/generate_outline', {
                project_id: project.id,
                description,
                length
            });

            if (response.data && response.data.table) {
                onOutlineGenerated(response.data.table);
                onClose();
            } else {
                setError('Failed to generate outline. Please try again.');
            }
        } catch (err) {
            console.error('Error generating outline:', err);
            setError(err.response?.data?.message || 'Error generating outline');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleInitiateGenerate = () => {
        setShowConfirmation(true);
    };

    const getLengthLabel = () => {
        switch (length) {
            case 1: return 'Very Short';
            case 2: return 'Short';
            case 3: return 'Medium';
            case 4: return 'Long';
            case 5: return 'Detailed';
            case 6: return 'Comprehensive';
            default: return 'Medium';
        }
    };

    return (
        <>
            <Popup 
                title="Generate Outline"
                onClose={onClose}
            >
                {error && <div className="form-error">{error}</div>}

                <div className="form-group">
                    <label htmlFor="description">Project Description</label>
                    <textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={isGenerating}
                        className="form-control"
                        placeholder="Describe what your project is about..."
                        rows={6}
                    ></textarea>
                </div>

                <div className="form-group">
                    <label htmlFor="length">Outline Length: {getLengthLabel()}</label>
                    <div className="popup-slider-container">
                        <input
                            type="range"
                            id="length"
                            min="1"
                            max="6"
                            step="1"
                            value={length}
                            onChange={(e) => setLength(parseInt(e.target.value))}
                            disabled={isGenerating}
                            className="popup-slider"
                        />
                        <div className="popup-slider-labels">
                            <span>Very Short</span>
                            <span>Short</span>
                            <span>Medium</span>
                            <span>Long</span>
                            <span>Detailed</span>
                            <span>Comprehensive</span>
                        </div>
                    </div>
                </div>

                <div className="warning-box">
                    <span className="material-symbols-outlined warning-icon">warning</span>
                    <p>Warning: Generating a new outline will replace all existing content in the editor. Make sure to save your work if needed.</p>
                </div>

                <div className="popup-buttons">
                    <button 
                        onClick={onClose}
                        className="popup-cancel-button"
                        disabled={isGenerating}
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleInitiateGenerate}
                        className="popup-primary-button"
                        disabled={isGenerating || !description.trim()}
                    >
                        {isGenerating ? 'Generating...' : 'Generate Outline'}
                    </button>
                </div>
            </Popup>

            {showConfirmation && (
                <Popup 
                    title="Confirm Outline Generation"
                    onClose={() => setShowConfirmation(false)}
                >
                    <p>Are you sure you want to generate a new outline? This will replace all existing content in the editor.</p>
                    
                    <div className="popup-buttons">
                        <button 
                            onClick={() => setShowConfirmation(false)}
                            className="popup-cancel-button"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleConfirmGenerate}
                            className="popup-primary-button"
                        >
                            Confirm
                        </button>
                    </div>
                </Popup>
            )}
        </>
    );
};

export default OutlineGeneratorPopup; 